// src/pages/Accueil.jsx
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ProductCard from '../components/product/ProductCard';
import {
  getFeaturedProducts,
  getReviews,
  getCategoriesWithImages
} from '../services/api';
import Popup from '../components/common/Popup';
import videoAccueil from '../assets/accueil.mp4';
import sig1 from '../assets/sig1.jpg';
import sig2 from '../assets/sig2.jpg';
import sig3 from '../assets/sig3.jpg';

const getImageUrl = (image) => {
  if (!image || typeof image !== 'string') return '';
  const clean = image.trim();
  if (/^https?:\/\//i.test(clean)) return clean;
  const filename = clean.split('/').filter(Boolean).pop();
  if (!filename) return '';
  return `https://madadline.com/api/product-images/${encodeURIComponent(filename)}`;
};

function Accueil() {
  const { t, i18n } = useTranslation();

  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newsletterOffer, setNewsletterOffer] = useState(null);
  const [exitOffer, setExitOffer] = useState(null);
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [showExit, setShowExit] = useState(false);

  /* ⭐ NEWSLETTER — formulaire bas de page */
  const [newsletterPhone, setNewsletterPhone] = useState('');
  const [newsletterConsent, setNewsletterConsent] = useState(false);
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterStatus, setNewsletterStatus] = useState(null); // 'success' | 'error' | null
  const [newsletterMessage, setNewsletterMessage] = useState('');

  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isN8nConnected, setIsN8nConnected] = useState(null);
  const [sessionId] = useState(() => Math.random().toString(36).substring(7));
  const messagesEndRef = useRef(null);
  const N8N_WEBHOOK_URL = 'https://developqi.app.n8n.cloud/webhook/madad-chatbot';

  /* ========== CHARGEMENT ========== */
  useEffect(() => {
    Promise.all([
      getFeaturedProducts().catch(() => []),
      getReviews(6).catch(() => []),
      getCategoriesWithImages().catch(() => []),
    ])
      .then(([prods, revs, cats]) => {
        setProducts(Array.isArray(prods) ? prods : []);
        setReviews(Array.isArray(revs) ? revs : []);
        setCategories(Array.isArray(cats) ? cats : []);
      })
      .catch((err) => console.error('Erreur chargement Accueil :', err))
      .finally(() => setLoading(false));
  }, []);

  /* ========== OFFRES ========== */
  useEffect(() => {
    const loadOffers = async () => {
      try {
        const [nlRes, exitRes] = await Promise.all([
          axios.get('https://madadline.com/api/offers/active?type=newsletter').catch(() => ({ data: null })),
          axios.get('https://madadline.com/api/offers/active?type=exit').catch(() => ({ data: null })),
        ]);
        if (nlRes.data?.id) setNewsletterOffer(nlRes.data);
        if (exitRes.data?.id) setExitOffer(exitRes.data);
      } catch (err) {
        console.error('Erreur chargement offres :', err);
      }
    };
    loadOffers();
  }, []);

  /* ========== POPUPS ========== */
  useEffect(() => {
    if (!newsletterOffer) return;
    const key = `offer_${newsletterOffer.id}_seen`;
    if (sessionStorage.getItem(key)) return;
    const timer = setTimeout(() => {
      setShowNewsletter(true);
      sessionStorage.setItem(key, 'true');
    }, 5000);
    return () => clearTimeout(timer);
  }, [newsletterOffer]);

  useEffect(() => {
    if (!exitOffer) return;
    const key = `offer_${exitOffer.id}_seen`;
    const handler = (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem(key)) {
        setShowExit(true);
        sessionStorage.setItem(key, 'true');
      }
    };
    document.addEventListener('mouseleave', handler);
    return () => document.removeEventListener('mouseleave', handler);
  }, [exitOffer]);

  /* ========== CHATBOT welcome ========== */
  useEffect(() => {
    setChatMessages([{
      id: 1,
      text: t('chat.welcome'),
      sender: 'bot',
      timestamp: new Date(),
    }]);
    testN8nConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const testN8nConnection = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      const response = await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatInput: 'ping',
          sessionId: 'test',
          test: true,
          timestamp: new Date().toISOString(),
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      setIsN8nConnected(response.ok);
      return response.ok;
    } catch {
      setIsN8nConnected(false);
      return false;
    }
  };

  const getFallbackResponse = (message) => {
    const msg = message.toLowerCase();
    if (msg.includes('prix') || msg.includes('tarif') || msg.includes('price'))
      return t('chat.fallbackPrices');
    if (msg.includes('livraison') || msg.includes('shipping') || msg.includes('توصيل'))
      return t('chat.fallbackShipping');
    if (msg.includes('taille') || msg.includes('size') || msg.includes('مقاس'))
      return t('chat.fallbackSizes');
    if (msg.includes('retour') || msg.includes('echange') || msg.includes('exchange') || msg.includes('استبدال'))
      return t('chat.fallbackExchange');
    if (msg.includes('bonjour') || msg.includes('salut') || msg.includes('hello') || msg.includes('مرحبا'))
      return t('chat.fallbackGreeting');
    return t('chat.fallbackDefault');
  };

  const handleSendMessage = async (customMessage = null) => {
    const messageToSend = customMessage || inputMessage;
    if (!messageToSend.trim()) return;

    setChatMessages(prev => [...prev, {
      id: Date.now(), text: messageToSend, sender: 'user', timestamp: new Date()
    }]);
    setInputMessage('');
    setIsTyping(true);

    try {
      const response = await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          chatInput: messageToSend,
          sessionId,
          timestamp: new Date().toISOString(),
        }),
      });

      const responseText = await response.text();
      if (!response.ok || !responseText.trim()) throw new Error('Réponse vide');

      let data;
      try { data = JSON.parse(responseText); }
      catch { data = { output: responseText }; }

      const botResponse = data.output || data.message || data.response || responseText;

      setChatMessages(prev => [...prev, {
        id: Date.now() + 1, text: botResponse, sender: 'bot', timestamp: new Date()
      }]);
    } catch {
      setChatMessages(prev => [...prev, {
        id: Date.now() + 1, text: getFallbackResponse(messageToSend), sender: 'bot', timestamp: new Date()
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const quickQuestions = [
    t('chat.quickPrices'),
    t('chat.quickShipping'),
    t('chat.quickSizes'),
    t('chat.quickExchange'),
  ];

  /* ⭐ Newsletter — soumission */
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus(null);
    setNewsletterMessage('');

    if (!newsletterPhone.trim()) {
      setNewsletterStatus('error');
      setNewsletterMessage('Veuillez entrer votre numéro.');
      return;
    }
    if (!newsletterConsent) {
      setNewsletterStatus('error');
      setNewsletterMessage('Merci de cocher la case pour accepter.');
      return;
    }

    setNewsletterSubmitting(true);
    try {
      const { data } = await axios.post(
        'https://madadline.com/api/newsletter/subscribe',
        { phone: newsletterPhone.trim(), consent: true }
      );
      setNewsletterStatus('success');
      setNewsletterMessage(data?.message || 'Merci ! Vous êtes bien inscrite.');
      setNewsletterPhone('');
      setNewsletterConsent(false);
    } catch (err) {
      const msg = err.response?.data?.error
        || err.response?.data?.errors?.phone?.[0]
        || 'Erreur lors de l’inscription. Merci de réessayer.';
      setNewsletterStatus('error');
      setNewsletterMessage(msg);
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  /* ========== CATÉGORIES ========== */
  const displayCategories = (categories || [])
    .filter(cat => cat && (cat.name || cat.categorie))
    .map((cat, i) => {
      const name = cat.name || cat.categorie;
      const slug = cat.slug || name.toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '-');
      const img = cat.image ? getImageUrl(cat.image) : '';
      return { key: slug || `cat-${i}`, label: name, img };
    });

  return (
    <main className="home">

      {/* HERO */}
      <section className="hero">
        <video className="hero__video" autoPlay loop muted playsInline>
          <source src={videoAccueil} type="video/mp4" />
        </video>
        <div className="hero__overlay" />
        <div className="hero__content">
          <span className="hero__label">{t('home.heroLabel')}</span>
          <h1 className="hero__title">
            {t('home.heroTitleLine1')}<br />
            {t('home.heroTitleLine2')}<br />
            <em>{t('home.heroTitleEm')}</em>
          </h1>
          <p className="hero__subtitle" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
            {t('home.heroSubtitle')}
          </p>
          <Link to="/boutique" className="hero__cta">
            {t('home.heroCta')}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </Link>
        </div>
      </section>

      {/* CATÉGORIES */}
      <section className="cats">
        <div className="cats__scroll">
          {loading ? (
            <div className="cats__loading"><div className="spinner" /></div>
          ) : displayCategories.length > 0 ? (
            displayCategories.map(c => (
              <Link key={c.key} to={`/${c.key}`} className="cats__item">
                <div className="cats__circle">
                  {c.img && (
                    <img
                      src={c.img}
                      alt={c.label}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const ph = e.currentTarget.parentNode.querySelector('.cats__circle-ph');
                        if (ph) ph.style.display = 'flex';
                      }}
                    />
                  )}
                  <div
                    className="cats__circle-ph"
                    style={{
                      display: c.img ? 'none' : 'flex',
                      width: '100%', height: '100%',
                      alignItems: 'center', justifyContent: 'center',
                      background: 'linear-gradient(135deg, #F8F6F0 0%, #EAE6DA 100%)',
                      borderRadius: '50%',
                      color: '#C21E56',
                      fontSize: 32,
                      fontWeight: 700,
                      fontFamily: 'var(--font-heading, serif)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {c.label.charAt(0).toUpperCase()}
                  </div>
                </div>
                <span>{c.label}</span>
              </Link>
            ))
          ) : (
            <p className="cats__empty">{t('home.catsEmpty')}</p>
          )}
        </div>
      </section>

      {/* RÉASSURANCE */}
      <section className="reassure">
        <div className="reassure__item">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="6" width="20" height="12" rx="2" />
            <circle cx="12" cy="12" r="2.5" />
          </svg>
          <span dangerouslySetInnerHTML={{ __html: t('home.reassurePayment') }} />
        </div>
        <div className="reassure__item">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="1" y="6" width="15" height="12" rx="1.5" />
            <path d="M16 10h4l3 3v5h-7" />
            <circle cx="5.5" cy="18" r="2" />
            <circle cx="18.5" cy="18" r="2" />
          </svg>
          <span dangerouslySetInnerHTML={{ __html: t('home.reassureDelivery') }} />
        </div>
        <div className="reassure__item">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="8" width="18" height="13" rx="1.5" />
            <path d="M12 8v13" />
            <path d="M3 12h18" />
            <path d="M12 8c-2 0-3.5-1.5-3.5-3S10 3 12 5c2-2 3.5-2 3.5 0S14 8 12 8z" />
          </svg>
          <span dangerouslySetInnerHTML={{ __html: t('home.reassureGift') }} />
        </div>
        <div className="reassure__item">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
          <span>{t('home.reassureWhatsapp')}</span>
        </div>
      </section>

      {/* NOUVELLE COLLECTION */}
      {(loading || products.length > 0) && (
        <section className="newcoll">
          <header className="newcoll__head">
            <h2 className="newcoll__title">{t('home.newCollTitle')}</h2>
            <Link to="/boutique" className="newcoll__see-all">
              {t('home.newCollSeeAll')} <span>›</span>
            </Link>
          </header>
          {loading ? (
            <div className="newcoll__loading"><div className="spinner" /></div>
          ) : (
            <div className="grid-products">
              {products.slice(0, 4).map(p => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* SIGNATURE */}
      <section className="signature">
        <header className="signature__head">
          <span className="signature__label">{t('home.signatureLabel')}</span>
          <h2 className="signature__title">{t('home.signatureTitle')}</h2>
        </header>
        <div className="signature__grid">
          <div className="signature__item">
            <img src={sig1} alt={t('home.sig1')} loading="lazy" />
            <p>{t('home.sig1')}</p>
          </div>
          <div className="signature__item">
            <img src={sig2} alt={t('home.sig2')} loading="lazy" />
            <p>{t('home.sig2')}</p>
          </div>
          <div className="signature__item">
            <img src={sig3} alt={t('home.sig3')} loading="lazy" />
            <p>{t('home.sig3')}</p>
          </div>
        </div>
      </section>

      {/* AVIS */}
      {reviews.length > 0 && (
        <section className="reviews">
          <header className="reviews__head"><h2>{t('home.reviewsTitle')}</h2></header>
          <div className="reviews__scroll">
            {reviews.map(r => (
              <article key={r.id} className="review-card">
                {r.image && (
                  <img
                    src={getImageUrl(r.image)}
                    alt={r.name || t('home.reviewsAlt')}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
                <div className="review-card__stars">★★★★★</div>
                <p>"{r.commentaire || r.comment}"</p>
                <span>{r.user?.name || r.name} · {r.city}</span>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* NEWSLETTER */}
      <section className="news">
        <h3>{t('home.newsTitle')}</h3>
        <p>{t('home.newsText')}</p>

        <form className="news__form" onSubmit={handleNewsletterSubmit}>
          <input
            type="tel"
            value={newsletterPhone}
            onChange={(e) => setNewsletterPhone(e.target.value)}
            placeholder={t('home.newsPlaceholder')}
            pattern="[+0-9 ]{10,}"
            required
            disabled={newsletterSubmitting}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={newsletterSubmitting}
          >
            {newsletterSubmitting ? '…' : t('home.newsButton')}
          </button>
        </form>

        <label className="news__consent">
          <input
            type="checkbox"
            checked={newsletterConsent}
            onChange={(e) => setNewsletterConsent(e.target.checked)}
            disabled={newsletterSubmitting}
          />
          <span>{t('home.newsConsent')}</span>
        </label>

        {newsletterStatus === 'success' && (
          <p style={{
            marginTop: 12,
            padding: '10px 14px',
            background: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: 8,
            color: '#166534',
            fontSize: 13,
            textAlign: 'center',
          }}>
            ✓ {newsletterMessage}
          </p>
        )}
        {newsletterStatus === 'error' && (
          <p style={{
            marginTop: 12,
            padding: '10px 14px',
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: 8,
            color: '#991b1b',
            fontSize: 13,
            textAlign: 'center',
          }}>
            {newsletterMessage}
          </p>
        )}
      </section>

      {/* POP-UP NEWSLETTER */}
      {newsletterOffer && (
        <Popup
          isOpen={showNewsletter}
          onClose={() => setShowNewsletter(false)}
          title={newsletterOffer.title}
          size="sm"
        >
          <div style={{ textAlign: 'center' }}>
            {newsletterOffer.image && (
              <img
                src={getImageUrl(newsletterOffer.image)}
                alt={newsletterOffer.title || 'Offre MADAD'}
                style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 10, marginBottom: 20, display: 'block' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <p style={{ marginBottom: 24, lineHeight: 1.6, color: '#555' }}>{newsletterOffer.message}</p>
            {newsletterOffer.code && (
              <div style={{
                padding: '14px 20px', background: '#F8F6F0',
                border: '2px dashed #C21E56', borderRadius: 10, marginBottom: 20,
                fontFamily: 'monospace', fontSize: 20, fontWeight: 700,
                color: '#C21E56', letterSpacing: 2,
              }}>{newsletterOffer.code}</div>
            )}
            <Link
              to={newsletterOffer.cta_link || '/boutique'}
              onClick={() => setShowNewsletter(false)}
              style={{
                display: 'block', width: '100%', padding: 16,
                background: 'linear-gradient(135deg, #C21E56, #a5184a)',
                color: '#F5F3E1', textDecoration: 'none', borderRadius: 10,
                fontWeight: 600, fontSize: 13, letterSpacing: 1,
                textTransform: 'uppercase', textAlign: 'center', boxSizing: 'border-box',
              }}
            >
              {newsletterOffer.cta_text || t('home.popupDefaultCta')}
            </Link>
            <button
              onClick={() => setShowNewsletter(false)}
              style={{
                marginTop: 12, background: 'none', border: 'none',
                color: '#888', fontSize: 12, cursor: 'pointer', textDecoration: 'underline',
              }}
            >
              {t('home.popupNoThanks')}
            </button>
          </div>
        </Popup>
      )}

      {/* POP-UP EXIT-INTENT */}
      {exitOffer && (
        <Popup
          isOpen={showExit}
          onClose={() => setShowExit(false)}
          title={exitOffer.title}
          size="sm"
        >
          <div style={{ textAlign: 'center' }}>
            {exitOffer.image && (
              <img
                src={getImageUrl(exitOffer.image)}
                alt={exitOffer.title || 'Offre MADAD'}
                style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 10, marginBottom: 20, display: 'block' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <p style={{ marginBottom: 24, lineHeight: 1.6, color: '#555' }}>{exitOffer.message}</p>
            {exitOffer.code && (
              <div style={{
                padding: '14px 20px', background: '#F8F6F0',
                border: '2px dashed #C21E56', borderRadius: 10, marginBottom: 20,
                fontFamily: 'monospace', fontSize: 20, fontWeight: 700,
                color: '#C21E56', letterSpacing: 2,
              }}>{exitOffer.code}</div>
            )}
            <Link
              to={exitOffer.cta_link || '/boutique'}
              onClick={() => setShowExit(false)}
              style={{
                display: 'block', width: '100%', padding: 16,
                background: 'linear-gradient(135deg, #C21E56, #a5184a)',
                color: '#F5F3E1', textDecoration: 'none', borderRadius: 10,
                fontWeight: 600, fontSize: 13, boxSizing: 'border-box',
              }}
            >
              {exitOffer.cta_text || t('home.popupDefaultCta')}
            </Link>
            <button
              onClick={() => setShowExit(false)}
              style={{
                marginTop: 12, background: 'none', border: 'none',
                color: '#888', fontSize: 12, cursor: 'pointer', textDecoration: 'underline',
              }}
            >
              {t('home.popupNoThanks')}
            </button>
          </div>
        </Popup>
      )}

    </main>
  );
}

export default Accueil;